/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  Typography,
  MenuItem,
} from '@mui/material';
import { Template, TemplateType } from '@marageti/z4-sdk/lib/templates/Template';
import { useApiClient, useFetch } from '@marageti/z4-lib';

const simplifyString = (string: string) => string.toLowerCase().replace(/[^a-z0-9]/g, '');

const DEFAULT_TEMPLATE_GROUPS: { type: TemplateType; label: string }[] = [
  { type: TemplateType.PersonalTemplate, label: '--- Personal ---' },
  { type: TemplateType.PartnershipsTemplate, label: '--- Partnerships ---' },
  { type: TemplateType.CareTemplate, label: '--- Care ---' },
];

const TEMPLATE_TYPE_LABELS: Record<TemplateType, string> = {
  [TemplateType.PersonalTemplate]: '--- Personal ---',
  [TemplateType.PartnershipsTemplate]: '--- Partnerships ---',
  [TemplateType.CareTemplate]: '--- Care ---',
  [TemplateType.TicketTemplate]: '--- Ticket ---',
  [TemplateType.PartnerTemplate]: '--- Partner ---',
};

type Props = {
  closeDialog: () => void,
  onSubmit: (text: string) => void,
  open: boolean,
  submitButtonText?: string,
  templateTypes?: TemplateType[],
};

const ChooseTemplateDialog = ({
  closeDialog,
  onSubmit,
  open,
  submitButtonText,
  templateTypes,
}: Props) => {
  const apiClient = useApiClient();

  const fetchConfig = useMemo(() => {
    if (!templateTypes) return undefined;
    const params = new URLSearchParams();
    templateTypes.forEach((t) => params.append('type', t));
    return { params };
  }, [templateTypes]);

  const { data: templates } = useFetch<Template[]>(
    apiClient.templatesClient.getTemplates,
    fetchConfig ? [fetchConfig] : [],
    true,
  );
  const [value, setValue] = useState({} as Template & { group: string } | null);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));

  const onChange = (e: React.SyntheticEvent<Element, Event>, selectedTemplate: Template & { group: string } | null) => {
    setValue(selectedTemplate);
  };

  const handleSubmit = () => {
    onSubmit(value?.body || '');
    setValue(null);
    closeDialog();
  };

  const sortedGroupedTemplates = useMemo(() => {
    if (!templates) return [];

    const templateGroups = templateTypes
      ? templateTypes.map((type) => ({ type, label: TEMPLATE_TYPE_LABELS[type] }))
      : DEFAULT_TEMPLATE_GROUPS;

    return templateGroups.flatMap(({ type, label }) => templates
      .filter((template) => template.templateType === type)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((template) => ({
        ...template,
        group: label,
      })));
  }, [templates, templateTypes]);

  const hasMultipleGroups = new Set(sortedGroupedTemplates.map((t) => t.group)).size > 1;

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth={isMobile} sx={{ '& .MuiDialog-paper': { minWidth: { desktop: '530px' } } }}>
      <DialogTitle>Choose Template</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={sortedGroupedTemplates}
          groupBy={hasMultipleGroups ? (option) => option.group : undefined}
          getOptionLabel={(option) => option.title || ''}
          value={value}
          onChange={onChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Template"
              fullWidth
              sx={{ minWidth: { desktop: '300px' } }}
            />
          )}
          renderOption={({ key, ...props }, option, _, ownerState) => (
            <MenuItem key={key} {...props}>
              <Typography variant="body1">{ownerState.getOptionLabel(option)}</Typography>
            </MenuItem>
          )}
          filterOptions={(options, params) => {
            const searchText = simplifyString(params.inputValue);
            return options.filter((option) => simplifyString(option.title).includes(searchText));
          }}
        />
      </DialogContent>
      <DialogActions sx={{ pt: 3 }}>
        <Button variant="contained" onClick={handleSubmit}>{submitButtonText || 'Use'}</Button>
        <Button variant="outlined" onClick={closeDialog}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChooseTemplateDialog;
